import { formatDate } from "@/lib/dateUtils";
import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, FileText, Clock, Eye } from "lucide-react";
import { CuteLoader } from "@/components/CuteLoader";

export default function HealthReimbursementAdmin() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [declineReason, setDeclineReason] = useState("");

  const loadSubmissions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/health-reimbursement/queue");
      setSubmissions(data);
    } catch (error) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleApprove = async () => {
    if (!effectiveDate) {
      toast.error("Effective date required");
      return;
    }

    setReviewing(true);
    try {
      await api.post("/admin/health-reimbursement/review", {
        employee_id: selectedSubmission.employee_id,
        action: "approve",
        effective_date: effectiveDate,
        expiration_date: expirationDate || null
      });
      toast.success("Approved successfully");
      setSelectedSubmission(null);
      setEffectiveDate("");
      setExpirationDate("");
      loadSubmissions();
    } catch (error) {
      toast.error("Failed to approve");
    } finally {
      setReviewing(false);
    }
  };

  const handleDecline = async () => {
    setReviewing(true);
    try {
      await api.post("/admin/health-reimbursement/review", {
        employee_id: selectedSubmission.employee_id,
        action: "decline",
        decline_reason: declineReason
      });
      toast.success("Declined");
      setSelectedSubmission(null);
      setDeclineReason("");
      loadSubmissions();
    } catch (error) {
      toast.error("Failed to decline");
    } finally {
      setReviewing(false);
    }
  };

  const viewDocument = (employeeId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/health-reimbursement/document/${employeeId}`;
    window.open(url, '_blank');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'under_review':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#B0905E]/10 text-[#B0905E] text-xs rounded"><Clock className="w-3 h-3" /> Under Review</span>;
      case 'approved':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case 'declined':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs rounded"><XCircle className="w-3 h-3" /> Declined</span>;
      case 'needs_renewal':
        return <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#9C6B4A]/10 text-[#9C6B4A] text-xs rounded"><Clock className="w-3 h-3" /> Needs Renewal</span>;
      default:
        return <span className="text-xs text-gray-500">{status}</span>;
    }
  };

  if (loading) {
    return <CuteLoader />;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F1] p-6 pb-24">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-serif text-[#231E1A] mb-2">Health Reimbursement Review</h1>
        <p className="text-sm text-[#9C6B4A] mb-6">Review employee insurance coverage submissions</p>

        {submissions.length === 0 && (
          <div className="text-center py-12 text-[#9C6B4A]">No submissions</div>
        )}

        <div className="grid gap-4">
          {submissions.map((submission) => (
            <div key={submission.employee_id} className="bg-white rounded-lg p-6 border border-[#EAE1D4]">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#231E1A]">{submission.employee_name}</h3>
                  <p className="text-sm text-[#9C6B4A]">
                    Submitted {formatDate(submission.submitted_at)}
                  </p>
                </div>
                {getStatusBadge(submission.status)}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => viewDocument(submission.employee_id)}
                  className="flex items-center gap-2 px-4 py-2 border border-[#9C6B4A] text-[#9C6B4A] rounded-lg hover:bg-[#FAF7F1] transition"
                >
                  <Eye className="w-4 h-4" />
                  View Document
                </button>

                {submission.status === 'under_review' && (
                  <button
                    onClick={() => setSelectedSubmission(submission)}
                    className="px-4 py-2 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition"
                  >
                    Review
                  </button>
                )}
              </div>

              {submission.status === 'approved' && (
                <div className="mt-4 pt-4 border-t border-[#EAE1D4] text-sm text-[#9C6B4A]">
                  Effective {formatDate(submission.effective_date)}
                  {submission.expiration_date && ` • Expires ${formatDate(submission.expiration_date)}`}
                </div>
              )}

              {submission.status === 'declined' && submission.decline_reason && (
                <div className="mt-4 pt-4 border-t border-[#EAE1D4] text-sm text-red-700">
                  Reason: {submission.decline_reason}
                </div>
              )}
            </div>
          ))}
        </div>

        {selectedSubmission && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-serif text-[#231E1A] mb-4">Review Submission</h2>
              <p className="text-[#9C6B4A] mb-6">{selectedSubmission.employee_name}</p>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">Effective Date</label>
                  <input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">Expiration Date (optional)</label>
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">Or Decline with Reason</label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Reason for decline..."
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A] resize-none"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={reviewing || !effectiveDate}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={handleDecline}
                  disabled={reviewing || !declineReason}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Decline
                </button>
                <button
                  onClick={() => {
                    setSelectedSubmission(null);
                    setEffectiveDate("");
                    setExpirationDate("");
                    setDeclineReason("");
                  }}
                  className="px-4 py-3 border border-[#EAE1D4] text-[#231E1A] rounded-lg hover:bg-[#FAF7F1] transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
